import { useGameContainer } from '../index';
import styles from './styles/GameResults.module.scss';
import LocalizedDate from 'components/LocalizedDate';
import { COMPLETED } from 'constants/GameStatuses';
import { useState } from 'react';
import TeamImage from 'components/TeamImage';
import cn from 'classnames';
import { GamesShowQuery } from 'queries';
import GameResultText from '@/components/GameResultText';
import ContentSection, { ContentSectionFlushContent, ContentSectionFooterButton, ContentSectionHeader } from '@/components/ContentSection';
import { SectionHeaderSize } from '@/components/SectionHeader';
import Table, { TableCell, TableHeaderCell, TableLinkRow, TableRow } from '@/components/Table';
import TextLink, { TextColorVariant, TextLinkSize } from '@/components/TextLink';
import Switch from '@/components/Switch';

function ResultsBlock({
  active,
  team,
  isCompleted
}: {
  active: boolean;
  team: GamesShowQuery['game']['gameTeams'][number]['team'];
  isCompleted: boolean;
}) {
  const games = isCompleted ? team.nextGames.nodes : team.previousGames.nodes;

  return (
    <div className={cn({ [styles.Inactive]: !active })}>
      <ContentSectionFlushContent>
        <Table
          className={styles.Table}
          header={
            <>
              <TableHeaderCell className={styles.SmallHeader}>Date</TableHeaderCell>
              <TableHeaderCell className={styles.SmallHeader}>Opponent</TableHeaderCell>
              <TableHeaderCell colSpan={2} className={styles.LargeHeader}>
                <div>
                  <TeamImage alt={team.name} src={team.image} />
                  {team.name} {isCompleted ? 'Upcoming' : 'Previous'} Games
                </div>
              </TableHeaderCell>
              <TableHeaderCell align="right">{isCompleted ? 'Time' : 'Result'}</TableHeaderCell>
            </>
          }
        >
          {games.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>No Games Scheduled</TableCell>
            </TableRow>
          )}
          {games.map(game => {
            const featured = game.gameTeams.find(gameTeam => gameTeam.team.id === team.id)!;
            const opponent = game.gameTeams.find(gameTeam => gameTeam.id !== featured.id)!;

            return (
              <TableLinkRow href={game.webPath} key={game.id}>
                <TableCell>
                  <LocalizedDate date={game.date} format="MMM D" />
                </TableCell>
                <TableCell>
                  <TextLink href={game.webPath}>
                    <span className={styles.Location}>{featured.isHome ? 'vs.' : '@'}</span>
                    <TeamImage src={opponent.team.image} alt={opponent.team.name} style={{ height: '20px', width: '20px' }} />
                    {opponent.team.name}
                  </TextLink>
                </TableCell>
                <TableCell align="right">
                  <GameResultText game={{ ...game, featured, opponent }}>{game.longStatusText}</GameResultText>
                </TableCell>
              </TableLinkRow>
            );
          })}
        </Table>
      </ContentSectionFlushContent>
      <ContentSectionFooterButton href={team.webPath}>See Full Schedule</ContentSectionFooterButton>
    </div>
  );
}

export default function GameResults() {
  const [{ game }] = useGameContainer();
  const gameTeams = game.gameTeams.filter(({ isTbd }) => !isTbd);
  const [selectedGameTeam, setSelectedGameTeam] = useState(gameTeams[0]);

  const isCompleted = game.statusId === COMPLETED;

  return (
    <ContentSection>
      <ContentSectionHeader size={SectionHeaderSize.LARGE}>{isCompleted ? 'Up Next' : 'Recent Results'}</ContentSectionHeader>
      <div className={styles.Toggle}>
        <Switch
          options={gameTeams}
          value={selectedGameTeam}
          onChange={setSelectedGameTeam}
          renderOption={({ team }) => (
            <>
              <TeamImage alt={team.name} src={team.image} style={{ height: '20px', width: '20px' }} />
              {isCompleted ? ' Next 5' : ' Last 5'}
            </>
          )}
        />
      </div>
      <div className={cn(styles.GamesContainer, { [styles.Full]: gameTeams.length === 1 })}>
        {gameTeams.map((gameTeam, i) => (
          <ResultsBlock key={gameTeam.id} active={gameTeam.id === selectedGameTeam.id} team={gameTeam.team} isCompleted={isCompleted} />
        ))}
      </div>
    </ContentSection>
  );
}
